<script>
  import { onMount, tick } from 'svelte';
  import { derived } from 'svelte/store';
  import { fade } from 'svelte/transition';
  import Breadcrumbs from '$components/Breadcrumbs/Breadcrumbs.svelte';
  import SavingsCard from '$components/SavingsCard/SavingsCard.svelte';
  import SavingsCardSkeletonTile from '$components/SavingsCardSkeletonTile/SavingsCardSkeletonTile.svelte';
  import GetCardTile from '$components/GetCardTile/GetCardTile.svelte';
  import TextExpander from '$components/TextExpander/TextExpander.svelte';
  import EmailCardOverpanel from '$components/EmailCardOverpanel/EmailCardOverpanel.svelte';
  import TextCardOverpanel from '$components/TextCardOverpanel/TextCardOverpanel.svelte';
  import AvailableLocationsOverpanel from '$components/AvailableLocationsOverpanel/AvailableLocationsOverpanel.svelte';
  import DrugOverpanel from '$components/DrugOverpanel/DrugOverpanel.svelte';
  import DrugFormOverpanel from '$components/DrugFormOverpanel/DrugFormOverpanel.svelte';
  import DrugStrengthOverpanel from '$components/DrugStrengthOverpanel/DrugStrengthOverpanel.svelte';
  import DrugQuantityOverpanel from '$components/DrugQuantityOverpanel/DrugQuantityOverpanel.svelte';
  import DrugSelectionOverpanel from '$components/DrugSelectionOverpanel/DrugSelectionOverpanel.svelte';
  import BaseLink from '$components/BaseLink/BaseLink.svelte';
  import BaseHeading from '$components/BaseHeading/BaseHeading.svelte';
  import BaseMarkdown from '$components/BaseMarkdown/BaseMarkdown.svelte';
  import LoadingPricingSkeletonTile from '$components/LoadingPricingSkeletonTile/LoadingPricingSkeletonTile.svelte';
  import PricingSkeletonTile from '$components/PricingSkeletonTile/PricingSkeletonTile.svelte';
  import PricingTile from '$components/PricingTile/PricingTile.svelte';
  import LocationPromo from '$components/LocationPromo/LocationPromo.svelte';
  import Separator from '$components/Separator/Separator.svelte';
  import PricingDrugDetailsControl from '$components/PricingDrugDetailsControl/PricingDrugDetailsControl.svelte';
  import LocationControl from '$components/LocationControl/LocationControl.svelte';
  import Wizard from '$components/Wizard/Wizard.svelte';
  import { openOverpanel, resetOverpanels } from '$stores/overpanelControl';
  import { forwardGeocoding } from '$utils/location';
  import handleSAASError from '$utils/handleSAASError';
  import { SPRITESHEET_URL } from '$utils/constants';
  import fetchPrices from '$utils/fetch-prices';
  import { StoredCard } from '$utils/local-storage';
  import { RetentionSent } from '$utils/session-storage';
  import fetchCard from '$libs/fetchCard';
  import { getPriceSet, getPriceForSort } from '$libs/getPriceSet';
  import currentLocation, { setCurrentLocation } from '$stores/currentLocation';
  import currentCard from '$stores/card';
  import { drug as drugStore, formValue, strengthValue, quantityValue, customQuantityValue } from '$stores/drug';
  import { setDrug, drugData, setForm, setStrength, setQuantity } from '$stores/drug';
  import { smoothScrollIntoView } from '$utils/side-effects';

  let { data = $bindable() } = $props();

  const lang = data.lang;
  let query = data.cardQuery;

  let initialStates = {
    idle: false,
    loading: false,
    finishing: false,
    error: false,
    error404: false,
    noDefaultPrices: false,
    noLocationPrices: false,
  };
  let cardData = $state();
  let prices = $state([]);
  let controllers = [];
  let loadingProps = $state({});
  let currentState = $state();
  let currForm = $state();
  let currStr = $state();
  let currQty = $state();
  let skeletonTilesRemaining = $state(0);
  let skeletonTilesUsed = $state(0);
  let totalCount;
  let failedCount;
  let completedRequests;
  let initialStr = $state();
  let initialQty = $state();
  let numberOfPricesToDisplay = $state(6);
  let smsEndpoint = `${process.env.SAAS_SMS_ENDPOINT}`;
  let emailEndpoint = `${process.env.SAAS_EMAIL_ENDPOINT}`;
  let pricingSubHeadingTextTemplate = $state();
  // we need to determine whether client-side JS is active in order to know which card to show
  // if we just have a <noscript> tag below without such a check, then two cards will appear on the page
  let isScript = typeof window !== 'undefined';

  drugData.subscribe((value) => {
    currForm = value.form;
    currStr = value.strength;
    currQty = value.quantity;
  });

  // if any store is change, set dirty to true
  // use clear() to set dirty to false
  let clear = () => {};
  const dirty = derived(
    [currentLocation, drugStore, formValue, strengthValue, quantityValue, customQuantityValue],
    (stores, set) => {
      clear = () => set(false);
      set(true);
    },
    false
  );
  
  const sortByName = (a, b) => (a.name > b.name ? 1 : -1);

  const sortByPrice = (a, b) => {
    const priceA = parseFloat(getPriceForSort(a.price.discounted, a.price.unc));
    const priceB = parseFloat(getPriceForSort(b.price.discounted, b.price.unc));
    return priceA === priceB ? sortByName(a, b) : priceA > priceB ? 1 : -1;
  };

  const sortByScore = (a, b) => {
    const aScore = a.score === undefined ? 0 : a.score;
    const bScore = b.score === undefined ? 0 : b.score;
    return aScore === bScore ? sortByPrice(a, b) : bScore - aScore;
  };

  const getDefaultPrices = () => {
    prices = [];

    // if default dose get default prices
    if (
      currStr.value === data.drug.defaultDose &&
      data.drug.defaultForm === currStr.doseForm &&
      currStr.quantity.filter((f) => f.selected === true)[0].value === currStr.defaultQuantity
    ) {
      if (data.defaultPrices && data.defaultPrices.prices.length) {
        prices.push(...data.defaultPrices.prices);
        numberOfPricesToDisplay = prices.length;
        setState('idle');
      } else {
        setState('noDefaultPrices');
      }
    }

    if ($currentLocation) {
      data.noPricesForZip.headingText = data.noPricesForZip.headingText.replace(/{{ZIP}}/, $currentLocation.zip);
      setState('noLocationPrices');
    }
  };

  const getNextPrices = ({ limit = 10, completedRequests, totalCount, failedCount }) => {
    loadingProps = {
      currentCount: prices.length,
      totalCount: totalCount - failedCount,
    };

    skeletonTilesRemaining = totalCount - failedCount - prices.length;

    if (prices.length < totalCount - failedCount) {
      let requests = 1;
      while (prices.length + requests * limit < totalCount - failedCount && requests < 4) {
        requests++;
      }
      getPrices(completedRequests * limit, limit, requests);
    } else {
      setState('finishing');
      loadingProps.success = true;
      window.setTimeout(() => {
        setState('idle');
      }, 1000);
    }
  };

  const getPrices = (index = 0, limit = 10, requests = 1) => {
    if (index === 0) {
      setState('loading');
      prices = [];
      loadingProps = {};
      failedCount = 0;
      completedRequests = 0;
    }

    const additionalHeaders = { 'x-bid': process.env.BUILD_TIMESTAMP };
    let zip;

    if ($currentLocation) {
      zip = $currentLocation.zip;
    }

    if (typeof window !== 'undefined' && window.location) {
      additionalHeaders['x-request-href'] = window.location.href;
    }

    numberOfPricesToDisplay = 6;
    controllers.length = 0;

    const pendingRequests = Array(requests)
      .fill(null)
      .map((value, requestIndex) => {
        const controller = new AbortController();
        const signal = controller.signal;
        controllers.push(controller);

        return fetchPrices(
          {
            ndc: currStr.value,
            quantity: currQty.value,
            zip,
            limit,
            index: index + limit * requestIndex,
            signal,
            additionalHeaders,
          },
          'Error fetching prices on the card page.'
        )
          .then((json) => {
            // remove one from pricing size to account for removed hd pricing
            totalCount = json.pagination.size - 1 - failedCount;
            if (totalCount <= 0 || json.pharmacies.length === 0) {
              // server response doesn't add up.
              throw { status: 404 };
            }

            let hdIndex = json.pharmacies.findIndex((pharmacy) => pharmacy.type === 'hd');

            // remove all hd pricing returns from list
            if (hdIndex !== -1) {
              json.pharmacies.splice(hdIndex, 1);
              totalCount++;
            }

            prices = [...prices, ...json.pharmacies].sort(sortByScore);

            // check if returned prices are default prices
            // default prices have pharmacies with count and score = null
            if (index === 0 && json.pharmacies[0].count === null && json.pharmacies[0].score === null) {
              if ($currentLocation) {
                data.noPricesForZip.headingText = data.noPricesForZip.headingText.replace(
                  /{{ZIP}}/,
                  $currentLocation.zip
                );
                setState('noLocationPrices');
              }

              numberOfPricesToDisplay = prices.length;
            }
          })
          .catch((err) => {
            if (err.name === 'AbortError') {
              return Promise.reject(err);
            }

            // if default prices exist update no prices for zip text and return default prices
            if (index === 0 && data.defaultPrices && data.defaultPrices.prices.length) {
              getDefaultPrices();
            } else {
              if ((err && err.status) || err.statusCode) {
                const error = data.errorList.find((error) => error.code === err.status || err.statusCode);

                if (error) {
                  loadingProps.failureMessage = error.message;
                }
              }

              if (index !== 0) {
                failedCount += limit;
              } else {
                setState(err && err.statusCode === 404 ? 'error404' : 'error');
                loadingProps.failure = true;
              }
            }
          });
      });

    Promise.all(pendingRequests)
      .then(() => {
        if (loadingProps.failure === true || state.noLocationPrices === true) return;

        if (index !== 0 || failedCount === 0) {
          completedRequests += requests;
          getNextPrices({ limit, completedRequests, totalCount, failedCount });
        } else {
          setState('error');
          loadingProps.failure = true;
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setState('error');
      });
  };

  const getURLVars = ({ npi, chainCode, chainLabel }) => {
    return {
      drug: data.drug.name,
      slug: data.drug.slug,
      form: JSON.stringify(currForm.value),
      strength: currStr.value,
      strengthLabel: currStr.label,
      quantity: currQty.value,
      quantityLabel: currQty.label,
      zip: $currentLocation && $currentLocation.zip,
      npi,
      chainCode,
      chainLabel,
    };
  };

  const setState = (state) => {
    currentState = { ...initialStates, [state]: true };
  };

  const manageSkeletonTiles = () => {
    skeletonTilesUsed++;

    return {
      destroy() {
        skeletonTilesUsed--;
      },
    };
  };

  const closeCallback = async () => {
    await tick();

    if ($dirty) {
      controllers.forEach((controller) => controller.abort());
      clear();

      if ($currentLocation && $currentLocation.zip) {
        data.noPricesForZip.headingText = data.noPricesForZip.headingText.replace(data.zip, $currentLocation.zip);
        pricingSubHeadingTextTemplate = data.pricingSubHeadingTextTemplate.replace(
          /<span>[\s\S]*?<\/span>/,
          `<span>${$currentLocation.city}, ${$currentLocation.state}</span>`
        );
        data.zip = $currentLocation.zip;

        getPrices();
      } else {
        getDefaultPrices();

        if (!prices.length) {
          getPrices();
        }
      }
    }
  };

  const handleLocationPromoClick = () => {
    openOverpanel('location', { closeCallback });
  };

  setState('loading');

  let state = $derived(currentState);
  let card = $derived(cardData);

  setDrug(data.drug);
  resetOverpanels();

  let expanded = $state(false);
  const toggle = (e) => {
    if (!expanded) {
      smoothScrollIntoView(e.target, { block: 'start' });
    } else {
      window.location = '#prices';
    }
    expanded = !expanded;
  };

  onMount(async () => {
    // load card from store
    let cardVal = $currentCard;

    // if no card is in the store load card from localStorage
    if (!cardVal) {
      try {
        cardVal = JSON.parse(StoredCard.get());
      } catch (err) {
        console.warn(err); /* eslint-disable-line no-console */
      }
    }

    if (data.doseSearchParam) {
      initialStr = data.doseSearchParam;
      data.drug.forms.forEach((form) => {
        currStr = form.doses.find((dose) => dose.value === initialStr);
        if (currStr) {
          currForm = form;
        }
      });
      
      setForm(currForm);
      setStrength(currStr);
    }

    if (data.qtySearchParams) {
      initialQty = data.qtySearchParams;
      setQuantity({ value: initialQty });
    }

      // if no card is in localStorage fetch card from SAAS
    // if location isnt set in story and query str had zip value geolocate zipcode
    // if dose and qty were in query fetch price
    const requests = [
      !cardVal ? fetchCard().catch(() => Promise.resolve()) : Promise.resolve(),
      data.zip && !$currentLocation
        ? forwardGeocoding({ value: data.zip }).catch(() => Promise.resolve())
        : Promise.resolve(),
    ];

    const [cardRet, locationRet] = await Promise.all(requests);

    if (!cardVal && cardRet) {
      if (cardRet.status < 400) {
        const json = await cardRet.json();
        cardVal = json.cards[0];
        StoredCard.set(JSON.stringify(cardVal));
        RetentionSent.set('false');
      } else {
        const { id } = await cardRet.json();
        handleSAASError('Error fetching card from SAAS endpoint', id);
      }
    }

    cardData = {
      card: cardVal || { ...data.defaultCard },
      smsEndpoint,
      emailEndpoint,
    };

    if (!$currentLocation && data.zip) {
      setCurrentLocation(locationRet[0]);
    }

    if (!data.zip && $currentLocation) {
      data.zip = $currentLocation.zip;
    }
    
    pricingSubHeadingTextTemplate = data.pricingSubHeadingTextTemplate.replace(
      /<span>[\s\S]*?<\/span>/,
      `<span>${$currentLocation.city}, ${$currentLocation.state}</span>`
    );
    
    if (initialStr || initialQty || $currentLocation) {
      getPrices();
    } else {
      getDefaultPrices();
    }

    clear();

    return () => resetOverpanels();
  });
</script>

<Breadcrumbs path={data.path} />
<div class="flex flex-col items-center w-full bg-gray-mercury lg:shadow-content-well">
  <div class="flex relative justify-center w-full bg-blue-very-light lg:shadow-content-well-sm lg:h-auto lg:py-12">
    <div class="flex flex-col lg:flex-row lg:grow items-center max-w-4xl">
      <div class="flex-1 my-6 mx-4 lg:my-0 lg:mr-16 lg:ml-0">
        <BaseHeading
          className="mb-2 text-xl text-center font-semibold text-blue-navy lg:mb-4 md:text-2xl lg:text-3xl lg:text-left lg:text-blue-dark-navy"
          headingLevel="h1"
        >
          {data.headingTextTemplate}
        </BaseHeading>
        <div class="text-center lg:text-left lg:text-xl text-blue-navy">
          {@html data.content}
        </div>
      </div>
      <div class="flex-1 w-full">
        <div class="mx-4 sm:mx-0 min-h-60 -mb-25 lg:mb-0">
          {#if isScript}
            {#if !card}
              <SavingsCardSkeletonTile {...data.savingsCard} />
            {:else}
              <SavingsCard {...data.savingsCard} {...card.card} />
            {/if}
          {/if}
          <noscript>
            <SavingsCard {...data.savingsCard} {...data.defaultCard} />
          </noscript>
        </div>
      </div>
    </div>
  </div>
  <GetCardTile {...data.getCardTile} />
  {#if data.printHelpText}
    <div class="hidden print:flex justify-center w-full pt-20 bg-white -mb-12">
      <BaseMarkdown {...data.printHelpText} />
    </div>
  {/if}
</div>
<div id="prices" class="print:hidden border-t bg-gray-mercury border-gray-light-silver shadow-content-well print:shadow-none">
  <div class="bg-white md:bg-transparent py-4 md:py-6 lg:py-10">
    <div class="w-full max-w-4xl mx-auto">
      <div class="text-center px-4">
        <h2 class="text-blue-dark-navy text-xl font-semibold lg:text-3xl">{data.pricingHeadingTextTemplate}</h2>
        <h3 class="mb-4 md:mb-6 text-blue-navy lg:mb-10 lg:text-lg">
          {@html $currentLocation ? pricingSubHeadingTextTemplate : data.noLocationPricingSubHeadingTextTemplate}
        </h3>
      </div>
      <h2 class="max-w-6xl mx-auto px-4 text-lg mb-2 font-semibold md:text-xl lg:px-0 lg:text-2xl text-blue-navy">
        {data.pricesHeadingTextTemplate}
      </h2>
      {#if data.drug.program}
        <div class="lg:mb-6 print:hidden">
          <Wizard {...data.wizard} />
        </div>
      {/if}
      <div class="lg:row-span-2 lg:col-span-2">
        <div class="bg-white md:bg-transparent">
          <div class="md:flex md:items-start md:justify-between">
            <PricingDrugDetailsControl
              {...data.pricingDrugDetailsControl}
              form={currForm.value}
              strength={currStr.label}
              quantity={currQty.label}
              {closeCallback}
            />
            <LocationControl {...data.locationControl} {closeCallback} />
          </div>
        </div>
        {#if state.noLocationPrices}
          <div class="mx-4 lg:mx-0">
            <LocationPromo
              {...data.noPricesForZip}
              icon="location-pin"
              onClick={() => openOverpanel('location', { closeCallback })}
            />
          </div>
        {/if}
        
        {#if state.loading || state.finishing}           
          <div class="mx-4 lg:mx-0" out:fade|local={{ duration: 500 }}>
            <LoadingPricingSkeletonTile
              {...data.loadingPricingSkeletonTile}
              {...loadingProps}
              retryAction={() => getPrices()}
            />
          </div>
        {/if}
        {#if state.idle || state.loading || state.finishing || state.noLocationPrices}
          <ul class="px-4 lg:px-0">
            {#if prices.length === 0 && !state.noLocationPrices}
              {#each Array(numberOfPricesToDisplay)}
                <li transition:fade|local={{ duration: 500 }}>
                  <PricingSkeletonTile />
                </li>
              {/each}
            {:else}
              {#each prices as price, index (index)}
                {#if prices[index - 1] && prices[index - 1].score > 0 && !prices[index].score}
                  <Separator text={data.pharmacySeparatorText} />
                {/if}
                <li
                  in:fade|local={{ duration: 500 }}
                  class:hidden={index > numberOfPricesToDisplay - 1 && !expanded}
                  class:block={index > numberOfPricesToDisplay - 1 && expanded}
                >
                  <PricingTile
                    {...data.pricingTile}
                    {...price}
                    {...getPriceSet(price.price.discounted, price.price.unc)}
                    {getURLVars}
                    hasLocation={!$currentLocation || state.noLocationPrices ? false : true}
                    {lang}
                    isGeneric={data.drug.slug === data.drug.genericSlug}
                  />
                </li>
                {#if state.loading && index % (numberOfPricesToDisplay - 1) === 0 && skeletonTilesUsed < skeletonTilesRemaining}
                  <li out:fade|local={{ duration: 500 }} use:manageSkeletonTiles>
                    <PricingSkeletonTile />
                  </li>
                {/if}
              {/each}
            {/if}
          </ul>
          {#if prices.length > numberOfPricesToDisplay}
            <button
              class="scroll-m-28 flex items-center mx-auto text-blue-cerulean font-semibold text-sm"
              onclick={toggle}
            >
              <svg class="w-3 h-3 mr-1 fill-current">
                <use xlink:href={`${SPRITESHEET_URL}#plus-sign`} />
              </svg>
              {#if !expanded}
                {data.morePricesText}
              {:else}
                {data.lessPricesText}
              {/if}
            </button>
          {/if}
        {/if}

        {#if state.error404 && !$currentLocation}
          <!-- no result after real time default pricing with custom quantity -->
          <div class="mx-4 lg:mx-0">
            <LocationPromo
              {...data.defaultPricesNotFound}
              icon="location-pin"
              onClick={() => openOverpanel('location', { closeCallback })}
            />
          </div>
        {:else if (state.error404 || state.error) && $currentLocation}
          <!-- no result after real time pricing with location (invalid NDC) -->
          <div class="mx-4 lg:mx-0">
            <LocationPromo
              {...data.drugNotFound}
              icon="find-drug"
              onClick={() => openOverpanel('drug', { closeCallback })}
            />
          </div>
        {:else if !$currentLocation || (state.noDefaultPrices && prices.length === 0)}
          <!-- init page load with no default prices -->
          {#if !state.noDefaultPrice && prices.length > 0}
            <hr class="hidden lg:block text-gray-silver my-8" />
          {/if}
          <div class="mx-4 lg:mx-0">
            <LocationPromo {...data.locationPromo} icon="location-pin" onClick={handleLocationPromoClick} />
          </div>
        {/if}
      </div>
    </div>
  </div>
</div>

{#if data.faq}
  <div
    class="px-4 flex flex-col items-center py-8 lg:py-15 print:hidden"
    itemscope={true}
    itemtype="https://schema.org/FAQPage"
  >
    <div class="flex flex-col w-full max-w-4xl">
      <BaseHeading
        className="text-base leading-tight font-semibold text-blue-dark-navy mb-8 lg:mb-9 lg:text-xl"
        headingLevel={data.faq.headingLevel}
      >
        {data.faq.headingText}
      </BaseHeading>
      <div class="mb-9">
        {#each data.faq.questions as question (question.id)}
          <TextExpander {...question} />
        {/each}
      </div>
      <BaseLink
        url={`${lang === 'es' ? '/es' : ''}/help`}
        className="print:hidden text-xs lg:text-sm leading-normal font-semibold text-blue-cerulean hover-hover:hover:text-blue-darkish 
        hover-hover:hover:underline active:text-blue-darkish active:underline"
      >
        {data.faq.readMoreText}
        <svg class="inline ml-1 md:ml-2 w-3 h-3 fill-current">
          <use xlink:href={`${SPRITESHEET_URL}#right-arrow`} />
        </svg>
      </BaseLink>
    </div>
  </div>
{/if}
{#if data.cardDisclaimer && data.cardDisclaimer.content}
  <div
    class="hidden print:block pt-20 pb-8 text-sm leading-tight text-gray-warm mx-auto max-w-4xl"
    data-testid="savings-card-page-disclaimer"
  >
    {@html data.cardDisclaimer.content}
  </div>
{/if}
<EmailCardOverpanel {...data.emailCardOverpanel} {...card} {query} {lang} />
<TextCardOverpanel {...data.textCardOverpanel} {...card} {query} {lang} />
<DrugOverpanel {...data.drugOverpanel} />
<DrugFormOverpanel {...data.drugFormOverpanel} />
<DrugStrengthOverpanel {...data.drugStrengthOverpanel} />
<DrugQuantityOverpanel {...data.drugQuantityOverpanel} />
<DrugSelectionOverpanel {...data.drugSelectionOverpanel} drugs={data.relatedDrugs} currentDrug={data.drug.slug}/>
<AvailableLocationsOverpanel {...data.availableLocationsOverpanel} {lang} />
